import { useState } from "react";
import type { TFunction } from "i18next";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import type { U3139DetailLot } from "../../../../U3139Service";

export interface U3139DetailLotsHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3139DetailLot>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3139DetailLot>>;
  dataChangedEvent: number;
}

export interface U3139DetailLotsHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3139DetailLot>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3139DetailLot>>;
}

export function u3139DetailLotsHandler(params: U3139DetailLotsHandlerParam): U3139DetailLotsHandler {
  const { gridStateHandlers, formStateHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dataChangedEvent, setDataChangedEvent] = useState(0);

  const onAdd = () => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  const onModify = () => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U3139DetailLot);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = () => setDeleteDialogOpen(true);

  const onDlgClose = (result: string) => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
      return;
    }
    const businessHandlers = formStateHandlers.getBusinessHandlers();
    if (businessHandlers?.validateForm) {
      const errors = businessHandlers.validateForm(formStateHandlers.getData());
      formStateHandlers.setErrors(errors);
      formStateHandlers.saveData();
      if (Object.keys(errors).length > 0) return;
    }
    if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
      const updatedData = gridStateHandlers
        .getData()
        .map((item) => (item.id === formStateHandlers.getData().id ? { ...formStateHandlers.getData(), idChecked: false } : item));
      gridStateHandlers.setData(updatedData);
      setDataChangedEvent((e) => e + 1);
    } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
      const newData = { ...formStateHandlers.getData(), id: generateTemporaryId(), idChecked: false };
      gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
      gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
      setDataChangedEvent((e) => e + 1);
    }
    formStateHandlers.afterSaveData(formStateHandlers.getData());
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string) => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((s) => s.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent((e) => e + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onDlgClose,
    isDialogOpen,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    formStateHandlers,
    gridStateHandlers,
    dataChangedEvent,
  };
}
